import type { ImportFormat, ImportKind, ImportOptionsInput, ImportRowStatus } from '@erp/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { api } from './api.ts';

// Types mirror the API's responses (apps/api/src/imports/imports.types.ts).

export type ImportStatus =
  'uploaded' | 'analyzed' | 'importing' | 'done' | 'undone' | 'cancelled' | 'expired';

export const IMPORT_STATUSES: Record<ImportStatus, string> = {
  uploaded: 'Falta el mapeo',
  analyzed: 'Lista para importar',
  importing: 'Importando',
  done: 'Terminada',
  undone: 'Deshecha',
  cancelled: 'Cancelada',
  expired: 'Vencida',
};

export interface ImportView {
  id: string;
  kind: ImportKind;
  status: ImportStatus;
  headers: string[];
  mapping: Record<string, number> | null;
  options: (ImportOptionsInput & { createDeals: boolean }) | null;
  totalRows: number;
  counts: Record<ImportRowStatus, number>;
  createdBy: string;
  createdAt: string;
  analyzedAt: string | null;
  finishedAt: string | null;
  undoneAt: string | null;
  undoUntil: string | null;
  hasData: boolean;
  suggestedMapping: Record<string, number> | null;
  sample: string[][] | null;
  /** RECONNECT's export (RE/MAX) is recognized by its columns and mapped automatically. */
  format: ImportFormat;
  /** RECONNECT, analyzed: names of the file to assign a district to (cantons...). */
  districtChoices: { name: string; department: string | null; rows: number }[] | null;
  /** RECONNECT, analyzed: agents that are not members (their rows go to the default). */
  unknownAgents: { name: string; rows: number }[] | null;
}

export interface ImportIssue {
  field: string | null;
  code: string;
  message: string;
  warning?: boolean;
}

export interface ImportRow {
  rowNumber: number;
  status: ImportRowStatus;
  issues: ImportIssue[];
  values: Record<string, string> | null;
  contactId: string | null;
  propertyId: string | null;
  dealId: string | null;
}

export const importKeys = {
  all: ['imports'] as const,
  list: ['imports', 'list'] as const,
  detail: (id: string) => ['imports', 'detail', id] as const,
  rows: (id: string, status: ImportRowStatus | undefined, page: number) =>
    ['imports', 'rows', id, status ?? null, page] as const,
};

export const useImports = () =>
  useQuery({ queryKey: importKeys.list, queryFn: () => api<ImportView[]>('/imports') });

export const useImport = (id: string) =>
  useQuery({
    queryKey: importKeys.detail(id),
    queryFn: () => api<ImportView>(`/imports/${id}`),
    retry: false,
  });

export const useImportRows = (id: string, status: ImportRowStatus | undefined, page: number) =>
  useQuery({
    queryKey: importKeys.rows(id, status, page),
    queryFn: () =>
      api<{ total: number; items: ImportRow[] }>(
        `/imports/${id}/rows?page=${String(page)}&pageSize=50${status ? `&status=${status}` : ''}`,
      ),
    placeholderData: keepPreviousData,
  });

/** Same-origin links: the browser sends the session cookie and saves the file. */
export const templateUrl = (kind: ImportKind) => `/api/imports/templates/${kind}`;
export const problemsUrl = (id: string) => `/api/imports/${id}/problems`;
